import React from "react";
import { Card } from "primereact/card";
import { TabView, TabPanel } from "primereact/tabview";
import { BreadCrumb } from "primereact/breadcrumb";
import { Accordion, AccordionTab } from "primereact/accordion";
import { HOME_ROUTE_PATH } from "../app_utils/route_paths/resolver/PageRoutes";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";

const CodeBlock = ({ children }: { children: string }) => (
  <pre
    style={{
      background: "#1e293b",
      color: "#e2e8f0",
      padding: "1rem",
      borderRadius: "6px",
      overflowX: "auto",
      fontSize: "0.85rem",
      lineHeight: "1.5",
      margin: "0.75rem 0",
    }}
  >
    <code>{children}</code>
  </pre>
);

const MethodBadge = ({ method }: { method: string }) => {
  const colors: Record<string, string> = {
    GET: "#22c55e",
    POST: "#3b82f6",
    PUT: "#f59e0b",
    DELETE: "#ef4444",
  };
  return (
    <span
      style={{
        background: colors[method] || "#6b7280",
        color: "#fff",
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "0.75rem",
        fontWeight: 700,
        marginRight: "0.5rem",
        fontFamily: "monospace",
      }}
    >
      {method}
    </span>
  );
};

const EndpointRow = ({
  method,
  path,
  auth,
  description,
}: {
  method: string;
  path: string;
  auth: string;
  description: string;
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      padding: "0.75rem 0",
      borderBottom: "1px solid #f1f5f9",
    }}
  >
    <MethodBadge method={method} />
    <div style={{ flex: 1 }}>
      <code style={{ fontSize: "0.85rem", fontWeight: 600 }}>{path}</code>
      <p style={{ margin: "0.25rem 0 0", color: "#64748b", fontSize: "0.8rem" }}>
        {description}
      </p>
    </div>
    <span
      style={{
        fontSize: "0.7rem",
        color: auth === "API Key" ? "#f59e0b" : "#3b82f6",
        background: auth === "API Key" ? "#fef3c7" : "#dbeafe",
        padding: "2px 8px",
        borderRadius: "4px",
        whiteSpace: "nowrap",
      }}
    >
      {auth}
    </span>
  </div>
);

const UserDocumentation = () => (
  <div style={{ padding: "0.5rem 0" }}>
    <h3>Integrating Apollos into Your Application</h3>
    <p>
      Apollos provides a unified API for sending SMS, collecting mobile money payments,
      and sending emails. This guide walks you through integrating each feature into your system.
    </p>

    <h3>Step 1: Create Your Account</h3>
    <ol style={{ lineHeight: "1.8" }}>
      <li>Visit the Apollos dashboard and click <strong>Register</strong>.</li>
      <li>Enter your email, name, and password.</li>
      <li>Check your email for an OTP verification code.</li>
      <li>Enter the OTP to activate your account.</li>
    </ol>

    <h3>Step 2: Get Your API Key</h3>
    <ol style={{ lineHeight: "1.8" }}>
      <li>Login to the Apollos dashboard.</li>
      <li>Navigate to <strong>API Keys</strong> from the sidebar menu.</li>
      <li>Click <strong>New API Key</strong>.</li>
      <li>Copy the generated key and store it securely. You will not be able to view it again.</li>
    </ol>

    <h3>Step 3: Top Up Your Wallet</h3>
    <ol style={{ lineHeight: "1.8" }}>
      <li>Navigate to <strong>Wallet</strong> from the sidebar.</li>
      <li>Enter the amount and your mobile money number.</li>
      <li>Confirm the payment on your phone.</li>
      <li>Your wallet balance will be updated once the payment is confirmed.</li>
    </ol>

    <h3>Step 4: Send Your First SMS</h3>
    <p>
      Use the public SMS endpoint to send messages. Include your API key in the{" "}
      <code>X-Apollos-Secret</code> header.
    </p>
    <CodeBlock>{`curl -X POST /api/v1/sms/send \\
  -H "Content-Type: application/json" \\
  -H "X-Apollos-Secret: your-api-key" \\
  -d '{
    "to": "256772123456",
    "message": "Hello from Apollos!"
  }'`}</CodeBlock>

    <h3>Step 5: Create Sub-Accounts (Shared Wallet)</h3>
    <p>
      Sub-accounts are user accounts that share your organization&apos;s wallet. Every SMS
      sent by a sub-account deducts credits from your (parent) account, and you can manage
      them all in one place. This is ideal for schools, clinics, or agencies that want to
      give teams their own login without giving away the master wallet.
    </p>
    <p>
      <strong>Any normal user</strong> can create sub-accounts under their own account. System
      administrators can also assign sub-accounts to any company (parent) account.
    </p>
    <ol style={{ lineHeight: "1.8" }}>
      <li>Navigate to <strong>Sub Accounts</strong> from the sidebar.</li>
      <li>Click <strong>Create Sub Account</strong>.</li>
      <li>Enter the sub-account&apos;s name, email, and initial password.</li>
      <li>
        Select the roles you want the sub-account to have. You can only assign roles whose
        permissions are a subset of your own permissions. If you do not select any roles,
        the sub-account automatically inherits your roles.
      </li>
      <li>Save. The sub-account logs in with its own credentials and shares your wallet.</li>
    </ol>

    <h3>How Sub-Account Billing Works</h3>
    <p>
      All charges (SMS credits, payment processing fees, etc.) are billed to the
      <strong>parent account</strong>, not the sub-account. Sub-accounts do not have their
      own wallets. When a sub-account sends an SMS or makes a payment, the system resolves
      the effective parent account and deducts from that wallet. This means you can manage
      all spending from a single balance.
    </p>

    <h3>Managing Your Sub-Accounts</h3>
    <p>
      As a normal user, you can view, edit, and delete sub-accounts under your account. When
      editing a sub-account, you can update its details or change its roles. You cannot
      assign permissions that you do not possess yourself.
    </p>

    <h3>Become an Account Owner (Company Account)</h3>
    <p>
      If you want your account to hold sub-accounts and let other systems create them
      programmatically, you can designate your account as an <strong>Account Owner</strong>.
      This turns your account into a company account that can provision sub-accounts via the
      public API. Contact your system administrator to set an <strong>Account Name</strong>{" "}
      and mark your user as an <strong>Account Owner</strong>. Once designated, you can
      generate an API secret and use it to create sub-accounts from your own applications.
    </p>

    <h3>Step 6: Check Your Wallet Balance</h3>
    <p>
      You can check your wallet balance and available credits at any time using the
      wallet API. For sub-accounts, this returns the parent account&apos;s balance since
      all charges are billed to the parent.
    </p>
    <CodeBlock>{`curl -X GET /api/v1/wallet/balance \\
  -H "X-Apollos-Secret: your-api-key"`}</CodeBlock>

    <h3>Step 7: Send Bulk SMS</h3>
    <p>
      You can send SMS to up to 1,000 recipients in a single request using the bulk
      endpoint. This is useful for marketing campaigns or notifications.
    </p>
    <CodeBlock>{`curl -X POST /api/v1/sms/bulk \\
  -H "Content-Type: application/json" \\
  -H "X-Apollos-Secret: your-api-key" \\
  -d '{
    "to": ["256772123456", "254787375269"],
    "message": "Promotional message content"
  }'`}</CodeBlock>

    <h3>Step 8: Send Emails</h3>
    <p>
      Send transactional or broadcast emails through the Apollos email API. You can send
      to a single recipient or broadcast to multiple recipients in one request.
    </p>
    <CodeBlock>{`curl -X POST /api/v1/email/send \\
  -H "Content-Type: application/json" \\
  -H "X-Apollos-Secret: your-api-key" \\
  -d '{
    "to": "customer@example.com",
    "subject": "Your Order Confirmation",
    "body_html": "<p>Thank you for your purchase!</p>"
  }'`}</CodeBlock>
    <p>
      For broadcast, use <code>recipients</code> instead of <code>to</code>:
    </p>
    <CodeBlock>{`curl -X POST /api/v1/email/send \\
  -H "Content-Type: application/json" \\
  -H "X-Apollos-Secret: your-api-key" \\
  -d '{
    "recipients": ["user1@example.com", "user2@example.com"],
    "subject": "Newsletter",
    "body_html": "<h1>Our Latest Updates</h1>"
   }'`}</CodeBlock>

    <h3>Rate Limits</h3>
    <p>
      All public API endpoints are rate-limited per API key to ensure fair usage. If you
      exceed the limit, you will receive a <code>429 Too Many Requests</code> response. Check
      the <code>Retry-After</code> header to see how long to wait before retrying.
    </p>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
      <thead>
        <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
          <th style={{ textAlign: "left", padding: "0.5rem" }}>Endpoint</th>
          <th style={{ textAlign: "left", padding: "0.5rem" }}>Limit</th>
        </tr>
      </thead>
      <tbody>
        <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
          <td style={{ padding: "0.5rem" }}>Send SMS</td>
          <td style={{ padding: "0.5rem" }}>100 messages per minute per API key</td>
        </tr>
        <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
          <td style={{ padding: "0.5rem" }}>Bulk SMS</td>
          <td style={{ padding: "0.5rem" }}>100 messages per minute per API key</td>
        </tr>
        <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
          <td style={{ padding: "0.5rem" }}>Send Email</td>
          <td style={{ padding: "0.5rem" }}>100 messages per minute per API key</td>
        </tr>
        <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
          <td style={{ padding: "0.5rem" }}>Payments</td>
          <td style={{ padding: "0.5rem" }}>60 requests per minute per API key</td>
        </tr>
        <tr>
          <td style={{ padding: "0.5rem" }}>Wallet / Sub-Accounts</td>
          <td style={{ padding: "0.5rem" }}>30 requests per minute per API key</td>
        </tr>
      </tbody>
    </table>

    <h3>Company Integration: Provision Sub-Accounts Automatically</h3>
    <p>
      If your organization has its own system, you can create sub-accounts
      programmatically. Each created sub-account automatically receives its own API key,
      so your system can immediately send on its behalf. All charges are billed to the
      parent (company) account.
    </p>
    <CodeBlock>{`# Create a sub-account (returns sub-account details + a one-time API key)
curl -X POST https://api.apollosms.com/api/v1/sub-accounts \\
  -H "X-Apollos-Secret: your-company-api-key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "username": "jane@company.com",
    "firstName": "Jane",
    "lastName": "Doe",
    "emailAddress": "jane@company.com",
    "phoneNumber": "256772123456",
    "initialPassword": "temporary-password",
    "roleIds": []
  }'`}</CodeBlock>

    <h3>SMS Integration</h3>
    <p>Send SMS messages to any phone number in supported countries through the Apollos gateway.</p>
    <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
      <EndpointRow method="POST" path="/api/v1/sms/send" auth="API Key" description="Send SMS to one or more recipients" />
      <EndpointRow method="POST" path="/api/v1/sms/bulk" auth="API Key" description="Send bulk SMS to up to 1,000 recipients" />
      <EndpointRow method="GET" path="/api/v1/sms/json" auth="Bearer" description="List SMS messages (dashboard / authenticated)" />
    </div>

    <h3 style={{ marginTop: "1.5rem" }}>Sub-Accounts API</h3>
    <p>
      Provision and manage sub-accounts from your own system. The parent account owns the
      credits, and each sub-account gets its own API key on creation.
    </p>
    <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
      <EndpointRow method="POST" path="/api/v1/sub-accounts" auth="API Key" description="Create a sub-account and issue its API key" />
      <EndpointRow method="GET" path="/api/v1/sub-accounts" auth="API Key" description="List your sub-accounts" />
      <EndpointRow method="DELETE" path="/api/v1/sub-accounts/{id}" auth="API Key" description="Delete a sub-account" />
    </div>

    <Accordion style={{ marginTop: "1rem" }}>
      <AccordionTab header="Send Single SMS">
        <p><strong>Request:</strong></p>
        <CodeBlock>{`POST /api/v1/sms/send
Content-Type: application/json

{
  "to": "256772123456",
  "message": "Your verification code is 123456"
}`}</CodeBlock>
        <p><strong>Parameters:</strong></p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
              <th style={{ textAlign: "left", padding: "0.5rem" }}>Field</th>
              <th style={{ textAlign: "left", padding: "0.5rem" }}>Type</th>
              <th style={{ textAlign: "left", padding: "0.5rem" }}>Required</th>
              <th style={{ textAlign: "left", padding: "0.5rem" }}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
              <td style={{ padding: "0.5rem" }}><code>to</code></td>
              <td style={{ padding: "0.5rem" }}>string</td>
              <td style={{ padding: "0.5rem" }}>Yes</td>
              <td style={{ padding: "0.5rem" }}>Recipient phone number in international format (no +)</td>
            </tr>
            <tr>
              <td style={{ padding: "0.5rem" }}><code>message</code></td>
              <td style={{ padding: "0.5rem" }}>string</td>
              <td style={{ padding: "0.5rem" }}>Yes</td>
              <td style={{ padding: "0.5rem" }}>SMS body (max 160 characters)</td>
            </tr>
          </tbody>
        </table>
      </AccordionTab>
      <AccordionTab header="Send Bulk SMS">
        <CodeBlock>{`POST /api/v1/sms/bulk
Content-Type: application/json

{
  "to": ["256772123456", "254787375269"],
  "message": "Promotional message content"
}`}</CodeBlock>
        <p style={{ marginTop: "0.5rem" }}>
          Supports up to 1,000 recipients per request. Each recipient receives the same
          message.
        </p>
      </AccordionTab>
    </Accordion>

    <h3 style={{ marginTop: "1.5rem" }}>Payment Integration</h3>
    <p>
      Collect mobile money payments via MTN and Airtel. You can also use Apollos as a
      payment processor in your own external systems — any application can initiate a
      charge on behalf of a customer using the public charge endpoint.
    </p>
    <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
      <EndpointRow method="POST" path="/api/v1/payments/charge" auth="API Key" description="Initiate mobile money collection from your own app or website" />
      <EndpointRow method="GET" path="/api/v1/payments/status/{reference}" auth="API Key" description="Check transaction status" />
      <EndpointRow method="GET" path="/api/v1/payments/lookup" auth="API Key" description="Verify phone number / account holder" />
      <EndpointRow method="GET" path="/api/v1/wallet/balance" auth="API Key" description="Check wallet balance and available credits" />
    </div>

    <Accordion style={{ marginTop: "1rem" }}>
      <AccordionTab header="Initiate Mobile Money Collection">
        <CodeBlock>{`POST /api/v1/payments/charge
X-Apollos-Secret: your-api-key
X-Apollos-Idempotency: unique-request-id
Content-Type: application/json

{
  "amount": 500000,
  "currency": "UGX",
  "payment_method": "mobile_money",
  "msisdn": "256772123456",
  "reference": "INV-2026-001"
}`}</CodeBlock>
        <p style={{ marginTop: "0.5rem" }}>
          Use this endpoint from your own application or website to charge customers.
          The <code>amount</code> is in the smallest currency unit (e.g., 500000 = 5,000 UGX).
          The <code>X-Apollos-Idempotency</code> header prevents duplicate charges. If you
          do not provide a <code>reference</code>, one will be generated automatically.
        </p>
      </AccordionTab>
    </Accordion>

    <h3 style={{ marginTop: "1.5rem" }}>Data Bundles</h3>
    <p>
      Purchase mobile data bundles for MTN, Airtel, and Lyca numbers through the KaCyber
      integration. The purchase cost is deducted from your wallet balance. These endpoints
      use dashboard JWT authentication.
    </p>
    <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
      <EndpointRow method="GET" path="/api/v1/data-bundles/catalogue" auth="Bearer" description="Browse the live bundle catalogue (optionally filter by telco)" />
      <EndpointRow method="POST" path="/api/v1/data-bundles/purchase" auth="Bearer" description="Purchase bundles for up to 200 recipients" />
      <EndpointRow method="GET" path="/api/v1/data-bundles/status/{reference}" auth="Bearer" description="Check the status of a bundle purchase" />
      <EndpointRow method="GET" path="/api/v1/data-bundles/ui" auth="Bearer" description="Paginated purchase history (dashboard reports)" />
    </div>

    <Accordion style={{ marginTop: "1rem" }}>
      <AccordionTab header="Purchase a Data Bundle">
        <CodeBlock>{`POST /api/v1/data-bundles/purchase
Authorization: Bearer your-jwt-token
Content-Type: application/json

{
  "externalReferenceID": "ORDER-2026-001",
  "Currency": "UGX",
  "recipients": [
    {
      "msisdn": "256772123456",
      "network": "MTN",
      "quantity": 1024,
      "unit": "MB",
      "validity": "Day"
    }
  ]
}`}</CodeBlock>
        <p style={{ marginTop: "0.5rem" }}>
          Networks: <code>MTN</code>, <code>AIRTEL</code>, <code>LYCA</code>. Units:
          <code> MB</code> or <code>GB</code>. Validity: <code>Day</code>, <code>Week</code>,
          or <code>Month</code>. Statuses are provider-driven (<code>pending</code>,
          <code> COMPLETED</code>, <code>FAILED</code>) — poll the status endpoint to refresh.
        </p>
      </AccordionTab>
    </Accordion>

    <h3 style={{ marginTop: "1.5rem" }}>Phone Number Format</h3>
    <p>All phone numbers must be in international format without the <code>+</code> prefix.</p>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
      <thead>
        <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
          <th style={{ textAlign: "left", padding: "0.5rem" }}>Country</th>
          <th style={{ textAlign: "left", padding: "0.5rem" }}>Format</th>
          <th style={{ textAlign: "left", padding: "0.5rem" }}>Example</th>
        </tr>
      </thead>
      <tbody>
        <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
          <td style={{ padding: "0.5rem" }}>Uganda</td>
          <td style={{ padding: "0.5rem" }}>256XXXXXXXXX</td>
          <td style={{ padding: "0.5rem" }}>256772123456</td>
        </tr>
        <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
          <td style={{ padding: "0.5rem" }}>Kenya</td>
          <td style={{ padding: "0.5rem" }}>254XXXXXXXXX</td>
          <td style={{ padding: "0.5rem" }}>254787375269</td>
        </tr>
        <tr>
          <td style={{ padding: "0.5rem" }}>Nigeria</td>
          <td style={{ padding: "0.5rem" }}>234XXXXXXXXXX</td>
          <td style={{ padding: "0.5rem" }}>2348012345678</td>
        </tr>
      </tbody>
    </table>

    <h3 style={{ marginTop: "1.5rem" }}>Code Examples</h3>
    <Accordion>
      <AccordionTab header="Python">
        <CodeBlock>{`import requests

API_URL = "http://apollos-sms.eu-north-1.elasticbeanstalk.com/api/v1"
API_KEY = "your-api-key"

# Send SMS
response = requests.post(
    f"{API_URL}/sms/send",
    json={
        "to": "256772123456",
        "message": "Hello from Apollos!"
    },
    headers={"X-Apollos-Secret": API_KEY}
)
print(response.json())

# Initiate payment collection
payment = requests.post(
    f"{API_URL}/payments/charge",
    json={
        "amount": 500000,
        "currency": "UGX",
        "payment_method": "mobile_money",
        "msisdn": "256772123456",
        "reference": "INV-001"
    },
    headers={
        "X-Apollos-Secret": API_KEY,
        "X-Apollos-Idempotency": "unique-request-id"
    }
)
print(payment.json())`}</CodeBlock>
      </AccordionTab>
      <AccordionTab header="Node.js">
        <CodeBlock>{`const axios = require('axios');

const API_URL = 'http://apollos-sms.eu-north-1.elasticbeanstalk.com/api/v1';
const API_KEY = 'your-api-key';

// Send SMS
const response = await axios.post(API_URL + '/sms/send', {
  to: '256772123456',
  message: 'Hello from Apollos!'
}, {
  headers: { 'X-Apollos-Secret': API_KEY }
});
console.log(response.data);

// Initiate payment collection
const payment = await axios.post(
  \`\${API_URL}/payments/charge\`,
  {
    amount: 500000,
    currency: "UGX",
    payment_method: "mobile_money",
    msisdn: "256772123456",
    reference: "INV-001"
  },
  {
    headers: {
      "X-Apollos-Secret": API_KEY,
      "X-Apollos-Idempotency": "unique-request-id"
    }
  }
);
console.log(payment.data);`}</CodeBlock>
      </AccordionTab>
      <AccordionTab header="cURL">
        <CodeBlock>{`# Send SMS
curl -X POST http://apollos-sms.eu-north-1.elasticbeanstalk.com/api/v1/sms/send \\
  -H "Content-Type: application/json" \\
  -H "X-Apollos-Secret: your-api-key" \\
  -d '{
    "to": "256772123456",
    "message": "Hello from Apollos!"
  }'

# Initiate payment
curl -X POST http://apollos-sms.eu-north-1.elasticbeanstalk.com/api/v1/payments/charge \\
  -H "Content-Type: application/json" \\
  -H "X-Apollos-Secret: your-api-key" \\
  -H "X-Apollos-Idempotency: unique-request-id" \\
  -d '{
    "amount": 500000,
    "currency": "UGX",
    "payment_method": "mobile_money",
    "msisdn": "256772123456",
    "reference": "INV-001"
  }'`}</CodeBlock>
      </AccordionTab>
    </Accordion>
  </div>
);

const AdminDocumentation = () => (
  <div style={{ padding: "0.5rem 0" }}>
    <h3>Administrator Guide</h3>
    <p>
      This guide covers everything in the User Guide plus how to configure SMS, email,
      and payment gateways as a system administrator.
    </p>

    <h3>Managing Gateways</h3>
    <p>
      As a Super Admin, you can configure SMS, email, and payment gateways to connect
      Apollos to various providers.
    </p>

    <Accordion>
      <AccordionTab header="SMS Gateway Configuration">
        <p>
          Navigate to <strong>Gateways</strong> in the sidebar to manage SMS providers.
          Each gateway connects to a specific SMS provider (Ego SMS, Vuyo SMS, Wiza SMS, KaCyber VAS, etc.).
        </p>
        <h4>Adding a New SMS Gateway</h4>
        <ol style={{ lineHeight: "1.8" }}>
          <li>Click <strong>Create Gateway</strong>.</li>
          <li>Fill in the gateway name, base URL, API credentials.</li>
          <li>Select the country and telecom provider codes this gateway supports.</li>
          <li>Set the priority (lower = preferred).</li>
          <li>Set the charge per SMS for this gateway.</li>
          <li>Activate the gateway.</li>
        </ol>
        <p><strong>Supported SMS Providers:</strong></p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
              <th style={{ textAlign: "left", padding: "0.5rem" }}>Provider</th>
              <th style={{ textAlign: "left", padding: "0.5rem" }}>Country</th>
              <th style={{ textAlign: "left", padding: "0.5rem" }}>API Method</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
              <td style={{ padding: "0.5rem" }}>Ego SMS</td>
              <td style={{ padding: "0.5rem" }}>Uganda</td>
              <td style={{ padding: "0.5rem" }}>POST JSON</td>
            </tr>
            <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
              <td style={{ padding: "0.5rem" }}>Vuyo SMS</td>
              <td style={{ padding: "0.5rem" }}>Uganda</td>
              <td style={{ padding: "0.5rem" }}>GET query params</td>
            </tr>
            <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
              <td style={{ padding: "0.5rem" }}>Wiza SMS</td>
              <td style={{ padding: "0.5rem" }}>Uganda</td>
              <td style={{ padding: "0.5rem" }}>GET query params</td>
            </tr>
            <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
              <td style={{ padding: "0.5rem" }}>KaCyber VAS</td>
              <td style={{ padding: "0.5rem" }}>Uganda</td>
              <td style={{ padding: "0.5rem" }}>POST JSON + Bearer</td>
            </tr>
            <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
              <td style={{ padding: "0.5rem" }}>Frog Ghana</td>
              <td style={{ padding: "0.5rem" }}>Ghana</td>
              <td style={{ padding: "0.5rem" }}>POST JSON</td>
            </tr>
            <tr>
              <td style={{ padding: "0.5rem" }}>Text SMS Kenya</td>
              <td style={{ padding: "0.5rem" }}>Kenya</td>
              <td style={{ padding: "0.5rem" }}>POST JSON</td>
            </tr>
          </tbody>
        </table>
      </AccordionTab>

      <AccordionTab header="Email Gateway Configuration">
        <p>
          Navigate to <strong>Email Gateways</strong> in the sidebar to manage email providers.
          Configure SMTP settings or third-party email services.
        </p>
        <h4>Adding a New Email Gateway</h4>
        <ol style={{ lineHeight: "1.8" }}>
          <li>Click <strong>Create Email Gateway</strong>.</li>
          <li>Enter the provider name (e.g., "Primary SMTP").</li>
          <li>Set the host, port, username, and password.</li>
          <li>Configure TLS/SSL settings.</li>
          <li>Set the sender email and name.</li>
          <li>Set the priority and activate.</li>
        </ol>
        <CodeBlock>{`POST /api/v1/email-gateways
Authorization: Bearer <jwt-token>
Content-Type: application/json

{
  "name": "Primary SMTP",
  "provider": "SMTP",
  "host": "smtp.gmail.com",
  "port": 587,
  "username": "your-email@gmail.com",
  "password": "your-app-password",
  "senderEmail": "noreply@yourcompany.com",
  "senderName": "Your Company",
  "useTls": true,
  "priority": 1
}`}</CodeBlock>
      </AccordionTab>

      <AccordionTab header="Payment Gateway Configuration">
        <p>
          Navigate to <strong>Payment Gateways</strong> in the sidebar to manage payment providers.
          Configure KaCyber Pay, Flutterwave, Pesapal, or Stripe.
        </p>
        <h4>Adding a New Payment Gateway</h4>
        <ol style={{ lineHeight: "1.8" }}>
          <li>Click <strong>Create Payment Gateway</strong>.</li>
          <li>Select the provider (KaCyber Pay, Flutterwave, etc.).</li>
          <li>Enter the API key, app ID, merchant ID.</li>
          <li>Set the callback secret for webhook verification.</li>
          <li>Set the base URL and priority.</li>
          <li>Choose a <strong>Product Type</strong>: <code>SMS_WALLET</code>, <code>DATA_BUNDLE</code>, <code>BANK_TRANSFER</code>, or <code>MOBILE_MONEY_DISBURSEMENT</code>.</li>
        </ol>
        <p>
          <strong>Note:</strong> Payment Gateways are the single source of truth. When you save a
          payment gateway with <code>productType = DATA_BUNDLE</code>, it is automatically synced
          to <strong>Data Bundle Gateways</strong>. Similarly, <code>BANK_TRANSFER</code> syncs to
          <strong>Bank Transfer Gateways</strong>, and <code>MOBILE_MONEY_DISBURSEMENT</code> syncs
          to <strong>Mobile Money Gateways</strong>.
        </p>
        <CodeBlock>{`POST /api/v1/payment-gateways
Authorization: Bearer <jwt-token>
Content-Type: application/json

{
  "name": "KaCyber Pay",
  "provider": "KACYBER",
  "productType": "BANK_TRANSFER",
  "apiKey": "your-api-key",
  "appId": "your-app-id",
  "merchantId": "your-merchant-id",
  "callbackSecret": "your-callback-secret",
  "baseUrl": "https://api.kacyber.africa",
  "priority": 1
}`}</CodeBlock>
      </AccordionTab>

      <AccordionTab header="Data Bundle Gateway Configuration">
        <p>
          Navigate to <strong>Data Bundle Gateways</strong> to manage data bundle providers.
          These are auto-synced from <strong>Payment Gateways</strong> when the product type
          is <code>DATA_BUNDLE</code>.
        </p>
        <CodeBlock>{`POST /api/v1/data-bundle-gateways
Authorization: Bearer <jwt-token>
Content-Type: application/json

{
  "name": "KaCyber Data Bundles",
  "provider": "KACYBER",
  "appId": "your-app-id",
  "apiKey": "your-api-key",
  "baseUrl": "https://api.kacyber.africa",
  "priority": 1
}`}</CodeBlock>
      </AccordionTab>

      <AccordionTab header="Bank Transfer Gateway Configuration">
        <p>
          Navigate to <strong>Bank Transfer Gateways</strong> to manage bank transfer providers.
          These are auto-synced from <strong>Payment Gateways</strong> when the product type
          is <code>BANK_TRANSFER</code>.
        </p>
        <CodeBlock>{`POST /api/v1/bank-transfer-gateways
Authorization: Bearer <jwt-token>
Content-Type: application/json

{
  "name": "KaCyber Bank Transfers",
  "provider": "KACYBER",
  "appId": "your-app-id",
  "apiKey": "your-api-key",
  "merchantId": "your-merchant-id",
  "callbackSecret": "your-callback-secret",
  "baseUrl": "https://api.kacyber.africa",
  "priority": 1
}`}</CodeBlock>
      </AccordionTab>

      <AccordionTab header="Mobile Money Gateway Configuration">
        <p>
          Navigate to <strong>Mobile Money Gateways</strong> to manage mobile money disbursement
          providers. These are auto-synced from <strong>Payment Gateways</strong> when the product
          type is <code>MOBILE_MONEY_DISBURSEMENT</code>.
        </p>
        <CodeBlock>{`POST /api/v1/mobile-money-gateways
Authorization: Bearer <jwt-token>
Content-Type: application/json

{
  "name": "KaCyber Mobile Money",
  "provider": "KACYBER",
  "appId": "your-app-id",
  "apiKey": "your-api-key",
  "merchantId": "your-merchant-id",
  "callbackSecret": "your-callback-secret",
  "baseUrl": "https://api.kacyber.africa",
  "priority": 1
}`}</CodeBlock>
      </AccordionTab>

      <AccordionTab header="Country Markup & Pricing">
        <p>
          Navigate to <strong>Country Markups</strong> to configure per-country pricing for SMS credits.
          This determines how much each SMS costs for users in different countries.
        </p>
        <h4>Setting Country Pricing</h4>
        <ol style={{ lineHeight: "1.8" }}>
          <li>Click <strong>Add New</strong>.</li>
          <li>Select the country.</li>
          <li>Enter the price per credit in the country's currency.</li>
          <li>Save the markup configuration.</li>
        </ol>
      </AccordionTab>

      <AccordionTab header="User Management">
        <p>
          Navigate to <strong>System Users</strong> to manage all users and their roles.
        </p>
        <h4>Creating a New User</h4>
        <ol style={{ lineHeight: "1.8" }}>
          <li>Click <strong>Create User</strong>.</li>
          <li>Enter the user's first name, last name, and username.</li>
          <li>Set a password for the user.</li>
          <li>Assign roles (Super Admin, Normal User, or custom roles).</li>
          <li>Save the user.</li>
        </ol>
        <h4>Creating Sub-Accounts</h4>
        <p>
          Navigate to <strong>Sub Accounts</strong> to create sub-accounts under your organization.
          Each sub-account gets its own credentials while sharing your gateway configuration.
        </p>
        <h4>Assigning a Parent Company</h4>
        <p>
          As a system administrator, when you create a sub-account you can pick the{" "}
          <strong>Parent Account (Company)</strong> from the dropdown. The sub-account then
          spends that company&apos;s wallet credits instead of your own. This is how you set
          up sub-accounts for a client company that manages its own system: assign the
          company as parent, and its public API key can provision sub-accounts that draw on
          its wallet.
        </p>
        <h4>Designating Account Owners</h4>
        <p>
          When editing a system user, you can set an <strong>Account Name (Company)</strong> and
          mark the user as an <strong>Account Owner</strong>. This turns the user into a company
          account that can hold sub-accounts and provision them via the public API.
        </p>
      </AccordionTab>

      <AccordionTab header="Webhook Configuration">
        <p>
          Configure webhook callbacks to receive real-time notifications when payments
          are completed or failed. Use the unified callback endpoint:
        </p>
        <CodeBlock>{`POST /api/v1/callbacks/KACYBER
X-Signature: <hmac-sha256-signature>
Content-Type: application/json

{
  "kacyberReference": "KC-99881",
  "status": "COMPLETED",
  "amount": 1000,
  "currency": "UGX"
}`}</CodeBlock>
        <p>
          Apollos verifies the signature using the callback secret from your gateway
          configuration, persists the callback to a unified registry, and retries
          processing on failure with exponential backoff.
        </p>
      </AccordionTab>

      <AccordionTab header="Callback Management">
        <p>
          Admins can inspect, replay, and monitor payment callbacks from the dashboard.
        </p>
        <ul style={{ lineHeight: "1.8" }}>
          <li><strong>Callbacks</strong> — view all callbacks with filters for provider, status, and date range</li>
          <li><strong>Replay</strong> — resend dead-lettered callbacks to reprocess them</li>
          <li><strong>Stats</strong> — see counts of received, processed, failed, and dead-lettered callbacks</li>
        </ul>
        <CodeBlock>{`# List callbacks
GET /api/v1/admin/callbacks?provider=KACYBER&status=FAILED

# Replay a dead-lettered callback
POST /api/v1/admin/callbacks/{id}/replay

# Get callback stats
GET /api/v1/admin/callbacks/stats`}</CodeBlock>
      </AccordionTab>

      <AccordionTab header="Provider Health & Callback Correlation">
        <p>
          Gateway health checks now track callback failure rates. If a payment gateway
          exceeds the callback failure threshold, it is marked as <strong>DEGRADED</strong>
          and an alert is created.
        </p>
        <ul style={{ lineHeight: "1.8" }}>
          <li><strong>Health Checks</strong> — run manually or on schedule via <code>Gateway Health</code></li>
          <li><strong>Alerts</strong> — unacknowledged alerts appear in the dashboard</li>
          <li><strong>Callback Metrics</strong> — each health check records total callbacks, failed callbacks, and failure rate</li>
        </ul>
        <CodeBlock>{`# Get latest health checks
GET /api/v1/gateway-health

# Get alerts
GET /api/v1/gateway-health/alerts

# Acknowledge an alert
POST /api/v1/gateway-health/alerts/{id}/acknowledge`}</CodeBlock>
      </AccordionTab>

      <AccordionTab header="Consumer Webhooks (Public API)">
        <p>
          Subscribe your own endpoint to receive <code>sms.sent</code>, <code>sms.failed</code>,
          or <code>payment.completed</code> events. These endpoints are part of the public
          API and authenticate with the <code>X-Apollos-Secret</code> header.
        </p>
        <CodeBlock>{`# Create a subscription (returns a one-time signingSecret)
POST /api/v1/webhooks/subscriptions
X-Apollos-Secret: <your-api-secret>
Content-Type: application/json

{
  "url": "https://your-app.example.com/webhooks",
  "events": "sms.sent,payment.completed"
}

# Rotate the signing secret
POST /api/v1/webhooks/subscriptions/{id}/rotate-secret

# Replay a failed / dead-lettered delivery
POST /api/v1/webhooks/replay/{deliveryId}`}</CodeBlock>
        <p>
          Each outgoing delivery includes <code>X-Apollos-Delivery-Id</code> and
          <code>X-Apollos-Signature</code> — hex(HMAC-SHA256(signingSecret, rawBody)) — so
          you can verify authenticity. Failed deliveries are retried with exponential
          backoff (30s, 1m, 2m, ...) and dead-lettered after 5 attempts; re-drive them any
          time with the replay endpoint above.
        </p>
      </AccordionTab>
    </Accordion>

    <h3 style={{ marginTop: "1.5rem" }}>How Gateway Routing Works</h3>
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        padding: "1.5rem",
        fontFamily: "monospace",
        fontSize: "0.85rem",
        lineHeight: "1.8",
        overflowX: "auto",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: "1rem", fontWeight: 700, fontSize: "1rem" }}>
        SMS Request Received
      </div>
      <div style={{ textAlign: "center", color: "#64748b" }}>
        &darr; Extract 5-digit provider code from phone number
      </div>
      <div style={{ textAlign: "center", color: "#64748b" }}>
        &darr; Match against gateway telecom codes (LIKE query)
      </div>
      <div style={{ textAlign: "center", color: "#64748b" }}>
        &darr; Select highest priority active gateway
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", flexWrap: "wrap", margin: "0.5rem 0" }}>
        {["Ego SMS", "Vuyo SMS", "Wiza SMS", "KaCyber", "Frog Ghana", "Text SMS Kenya"].map((p) => (
          <span
            key={p}
            style={{
              background: "#e2e8f0",
              padding: "4px 10px",
              borderRadius: "4px",
              fontSize: "0.75rem",
            }}
          >
            {p}
          </span>
        ))}
      </div>
      <div style={{ textAlign: "center", color: "#64748b" }}>
        &darr; Send SMS via provider API
      </div>
      <div style={{ textAlign: "center", fontWeight: 700, fontSize: "1rem" }}>
        Customer&apos;s Phone
      </div>
    </div>
  </div>
);

const Documentation = () => {
  const breadcrumbHome = {
    icon: "pi pi-home",
    command: () => {
      window.location.href = HOME_ROUTE_PATH;
    },
  };

  const isSuperAdmin = UserSessionUtils.isSuperAdmin();

  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        alert("Failed to download " + filename);
        return;
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert("Failed to download " + filename);
    }
  };

  return (
    <div className="grid">
      <div className="col-12">
        <Card>
          <BreadCrumb home={breadcrumbHome} model={[{ label: "Documentation" }]} />
          <h2 style={{ margin: "1rem 0 0.25rem" }}>Apollos Documentation</h2>
          <p style={{ color: "#64748b", marginBottom: "1.5rem" }}>
            {isSuperAdmin
              ? "System administrator guide for managing gateways, users, and platform configuration."
              : "Integration guide for sending SMS, collecting payments, and managing your API keys."}
          </p>

          <TabView>
            <TabPanel header="Overview">
              <div style={{ padding: "0.5rem 0" }}>
                <h3>What is Apollos?</h3>
                <p>
                  Apollos is a unified communication and payment platform that lets businesses and
                  developers send SMS messages and collect mobile money payments across multiple
                  countries and telecom providers through a single, simple API.
                </p>
                <p>
                  Instead of integrating with each SMS provider and payment gateway separately,
                  Apollos aggregates them behind one interface. You integrate once and get access
                  to all supported providers automatically.
                </p>

                <h3>Why Integrate with Apollos?</h3>
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <Card title="Single API, Multiple Providers" style={{ height: "100%" }}>
                      <p style={{ margin: 0 }}>
                        Send SMS through 6+ providers with one API call. Apollos routes messages
                        automatically based on the destination phone number.
                      </p>
                    </Card>
                  </div>
                  <div className="col-12 md:col-6">
                    <Card title="Mobile Money Payments" style={{ height: "100%" }}>
                      <p style={{ margin: 0 }}>
                        Accept MTN Mobile Money and Airtel Money payments in Uganda and Kenya. The
                        payment provider is abstracted — you just call a simple collection API and
                        receive a webhook callback.
                      </p>
                    </Card>
                  </div>
                  <div className="col-12 md:col-6">
                    <Card title="Automatic Routing" style={{ height: "100%" }}>
                      <p style={{ margin: 0 }}>
                        Phone numbers are automatically matched to the correct gateway based on
                        country and telecom prefix. No manual configuration needed per provider.
                        Apollos handles the routing transparently.
                      </p>
                    </Card>
                  </div>
                  <div className="col-12 md:col-6">
                    <Card title="Built-in Wallet" style={{ height: "100%" }}>
                      <p style={{ margin: 0 }}>
                        Top up your wallet via mobile money, track balances, and manage credits.
                        Country-level pricing markups are handled automatically.
                      </p>
                    </Card>
                  </div>
                  <div className="col-12 md:col-6">
                    <Card title="Callback Management" style={{ height: "100%" }}>
                      <p style={{ margin: 0 }}>
                        All payment callbacks are persisted to a unified registry with signature
                        verification, automatic retry with exponential backoff, and a dead-letter
                        queue. Admins can inspect and replay callbacks from the dashboard.
                      </p>
                    </Card>
                  </div>
                </div>

                <h3>Supported Countries</h3>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.85rem",
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>Country</th>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>SMS Providers</th>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>Mobile Money</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}>Uganda</td>
                      <td style={{ padding: "0.5rem" }}>Ego SMS, Vuyo SMS, Wiza SMS, Kacyber VAS</td>
                      <td style={{ padding: "0.5rem" }}>MTN, Airtel</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}>Kenya</td>
                      <td style={{ padding: "0.5rem" }}>Text SMS Kenya</td>
                      <td style={{ padding: "0.5rem" }}>Coming soon</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "0.5rem" }}>Nigeria</td>
                      <td style={{ padding: "0.5rem" }}>Available via gateway config</td>
                      <td style={{ padding: "0.5rem" }}>Coming soon</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </TabPanel>

            <TabPanel header="Getting Started">
              <UserDocumentation />
            </TabPanel>

            <TabPanel header="Developers">
              <div style={{ padding: "0.5rem 0" }}>
                <h3>Using Apollos as a Developer</h3>
                <p>
                  If you are a developer integrating Apollos into your own application,
                  this guide explains how to authenticate, use API keys, and make
                  programmatic requests to the Apollos platform.
                </p>

                <h3>What is an API Key?</h3>
                <p>
                  An API key is a unique, secret token that identifies your application
                  when making requests to the Apollos API. It serves as your
                  authentication credential for programmatic (server-to-server) access.
                  Every API request you make must include a valid API key so that
                  Apollos can verify who is making the request and authorize the action.
                </p>

                <h3>Why Do You Need to Enter a Name for Your API Key?</h3>
                <p>
                  When you create an API key, you are asked to give it a name. This name
                  is for <strong>your identification only</strong> — it is not sent to
                  Apollos or included in API requests. The name helps you:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Identify</strong> which key belongs to which application or
                    service (e.g., "Production SMS Service", "Staging Test Key").
                  </li>
                  <li>
                    <strong>Track usage</strong> — each key has its own usage count and
                    rate limits, so you can monitor which application is consuming how
                    many credits.
                  </li>
                  <li>
                    <strong>Manage keys</strong> — if a key is compromised or no longer
                    needed, you can delete it by its name without affecting other keys.
                  </li>
                  <li>
                    <strong>Rotate keys</strong> — you can create a new key with a new
                    name, update your application, and then delete the old key.
                  </li>
                </ul>

                <h3>How to Get Your API Key</h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Login to the Apollos dashboard.</li>
                  <li>Navigate to <strong>API Keys</strong> from the sidebar menu.</li>
                  <li>Click <strong>New API Key</strong>.</li>
                  <li>Enter a descriptive name (e.g., "My App Production Key").</li>
                  <li>Click Save. The system will generate a key and display it once.</li>
                  <li>
                    <strong>Copy and store it securely.</strong> You will not be able to
                    view it again after the dialog closes.
                  </li>
                </ol>

                <h3>How to Use Your API Key in Requests</h3>
                <p>
                  Include your API key in the request body as <code>apiKey</code> for
                  SMS endpoints, or use the <code>Authorization</code> header for
                  dashboard/authenticated endpoints.
                </p>

                <Accordion>
                  <AccordionTab header="Send SMS (API Key in Body)">
                    <CodeBlock>{`POST /api/v1/sms/json
Content-Type: application/json

{
  "apiKey": "your-api-key",
  "messages": [
    {
      "message": "Hello from Apollos!",
      "phoneNumbers": ["256772123456"]
    }
  ]
}`}</CodeBlock>
                  </AccordionTab>
                  <AccordionTab header="Authenticated Endpoints (Bearer Token)">
                    <CodeBlock>{`POST /api/v1/payments/collection
Authorization: Bearer <jwt-token>
Content-Type: application/json

{
  "externalReferenceID": "INV-001",
  "customerName": "Jane Doe",
  "narration": "Wallet top-up",
  "currency": "UGX",
  "amount": 5000,
  "msisdn": "256772123456",
  "network": "MTN"
}`}</CodeBlock>
                  </AccordionTab>
                </Accordion>

                <h3>Sub-Account Provisioning API</h3>
                <p>
                  If your system manages multiple users or tenants, you can create sub-accounts
                  on behalf of your Apollos account. A sub-account shares the parent&apos;s
                  wallet and gets its own API key on creation. All endpoints authenticate with
                  the <code>X-Apollos-Secret</code> header (the company&apos;s API secret).
                </p>
                <Accordion>
                  <AccordionTab header="Create a Sub-Account">
                    <CodeBlock>{`POST /api/v1/sub-accounts
X-Apollos-Secret: <your-api-secret>
Content-Type: application/json

{
  "username": "jane@company.com",
  "firstName": "Jane",
  "lastName": "Doe",
  "emailAddress": "jane@company.com",
  "phoneNumber": "256772123456",
  "initialPassword": "temporary-password",
  "roleIds": []
}`}</CodeBlock>
                    <p style={{ marginTop: "0.5rem" }}>
                      Response includes the new sub-account and a one-time{" "}
                      <code>apiKey</code>. Store it immediately — it will not be shown again.
                    </p>
                  </AccordionTab>
                  <AccordionTab header="List Sub-Accounts">
                    <CodeBlock>{`GET /api/v1/sub-accounts
X-Apollos-Secret: <your-api-secret>`}</CodeBlock>
                  </AccordionTab>
                  <AccordionTab header="Delete a Sub-Account">
                    <CodeBlock>{`DELETE /api/v1/sub-accounts/{id}
X-Apollos-Secret: <your-api-secret>`}</CodeBlock>
                  </AccordionTab>
                </Accordion>
                <p style={{ marginTop: "0.75rem" }}>
                  If the API secret belongs to a sub-account, provisioning is scoped up to the
                  parent account automatically, so sub-accounts of sub-accounts are always
                  created under the company that owns the credits.
                </p>

                <h3>Webhooks (Receiving Events)</h3>
                <p>
                  Subscribe your own endpoint to receive real-time notifications for events
                  like <code>sms.sent</code>, <code>sms.failed</code>, and
                  <code>payment.completed</code>. These webhooks authenticate with the{" "}
                  <code>X-Apollos-Secret</code> header.
                </p>
                <Accordion>
                  <AccordionTab header="Create a Webhook Subscription">
                    <CodeBlock>{`POST /api/v1/webhooks/subscriptions
X-Apollos-Secret: <your-api-secret>
Content-Type: application/json

{
  "url": "https://your-app.example.com/webhooks",
  "events": "sms.sent,payment.completed"
}`}</CodeBlock>
                    <p style={{ marginTop: "0.5rem" }}>
                      The response includes a one-time <code>signingSecret</code>. Use it to
                      verify that incoming webhooks are authentic. Each outgoing delivery
                      includes <code>X-Apollos-Delivery-Id</code> and{" "}
                      <code>X-Apollos-Signature</code> headers.
                    </p>
                  </AccordionTab>
                  <AccordionTab header="Verify Webhook Signatures">
                    <CodeBlock>{`import hmac
import hashlib

def verify_webhook(body, signature, signing_secret):
    expected = hmac.new(
        signing_secret.encode(),
        body.encode(),
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, signature)`}</CodeBlock>
                  </AccordionTab>
                  <AccordionTab header="Rotate Signing Secret">
                    <CodeBlock>{`POST /api/v1/webhooks/subscriptions/{id}/rotate-secret
X-Apollos-Secret: <your-api-secret>`}</CodeBlock>
                  </AccordionTab>
                </Accordion>

                <h3>API Key Best Practices</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    Never expose your API key in client-side code (browsers, mobile
                    apps). Always call the API from your backend server.
                  </li>
                  <li>
                    Use different API keys for different environments (development,
                    staging, production).
                  </li>
                  <li>
                    Rotate your keys periodically. Create a new key, update your
                    application, then delete the old key.
                  </li>
                  <li>
                    Each API key has its own rate limits and usage tracking, so
                    naming keys clearly helps you monitor consumption per application.
                  </li>
                </ul>

                <h3>Code Examples</h3>
                <Accordion>
                  <AccordionTab header="Python">
                    <CodeBlock>{`import requests

API_URL = "http://apollos-sms.eu-north-1.elasticbeanstalk.com/api/v1"
API_KEY = "your-api-key"

# Send SMS
response = requests.post(
    f"{API_URL}/sms/send",
    json={
        "to": "256772123456",
        "message": "Hello from Apollos!"
    },
    headers={"X-Apollos-Secret": API_KEY}
)
print(response.json())`}</CodeBlock>
                  </AccordionTab>
                   <AccordionTab header="Node.js">
                     <CodeBlock>{`const axios = require('axios');

const API_URL = 'http://apollos-sms.eu-north-1.elasticbeanstalk.com/api/v1';
const API_KEY = 'your-api-key';

// Send SMS
const response = await axios.post(API_URL + '/sms/send', {
  to: '256772123456',
  message: 'Hello from Apollos!'
}, {
  headers: { 'X-Apollos-Secret': API_KEY }
});
console.log(response.data);`}</CodeBlock>
                  </AccordionTab>
                  <AccordionTab header="cURL">
                    <CodeBlock>{`curl -X POST http://apollos-sms.eu-north-1.elasticbeanstalk.com/api/v1/sms/send \\
  -H "Content-Type: application/json" \\
  -H "X-Apollos-Secret: your-api-key" \\
  -d '{
    "to": "256772123456",
    "message": "Hello from Apollos!"
  }'`}</CodeBlock>
                  </AccordionTab>
                </Accordion>
              </div>
            </TabPanel>

            <TabPanel header="Vibe Coders">
              <div style={{ padding: "0.5rem 0" }}>
                <h3>AI-Assisted Development with Apollos</h3>
                <p>
                  This tab is optimized for <strong>vibe coders</strong> — developers who build
                  with AI pair programmers (Kilo, Cursor, Copilot, etc.). Use the quick reference
                  below to give your AI assistant the exact endpoints, field names, and patterns
                  it needs to generate working code.
                </p>

                <h3>Base URL</h3>
                <CodeBlock>{`http://apollos-sms.eu-north-1.elasticbeanstalk.com/api/v1`}</CodeBlock>

                <h3 style={{ marginTop: "1.5rem" }}>Quick Reference Table</h3>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>Action</th>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>Method</th>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>Endpoint</th>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>Auth</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}>Send SMS</td>
                      <td style={{ padding: "0.5rem" }}>POST</td>
                      <td style={{ padding: "0.5rem" }}><code>/sms/send</code></td>
                      <td style={{ padding: "0.5rem" }}>X-Apollos-Secret</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}>Bulk SMS</td>
                      <td style={{ padding: "0.5rem" }}>POST</td>
                      <td style={{ padding: "0.5rem" }}><code>/sms/bulk</code></td>
                      <td style={{ padding: "0.5rem" }}>X-Apollos-Secret</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}>Send Email</td>
                      <td style={{ padding: "0.5rem" }}>POST</td>
                      <td style={{ padding: "0.5rem" }}><code>/email/send</code></td>
                      <td style={{ padding: "0.5rem" }}>X-Apollos-Secret</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}>Charge (mobile money)</td>
                      <td style={{ padding: "0.5rem" }}>POST</td>
                      <td style={{ padding: "0.5rem" }}><code>/payments/charge</code></td>
                      <td style={{ padding: "0.5rem" }}>X-Apollos-Secret</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}>Wallet balance</td>
                      <td style={{ padding: "0.5rem" }}>GET</td>
                      <td style={{ padding: "0.5rem" }}><code>/wallet/balance</code></td>
                      <td style={{ padding: "0.5rem" }}>X-Apollos-Secret</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}>List sub-accounts</td>
                      <td style={{ padding: "0.5rem" }}>GET</td>
                      <td style={{ padding: "0.5rem" }}><code>/sub-accounts</code></td>
                      <td style={{ padding: "0.5rem" }}>X-Apollos-Secret</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}>Create sub-account</td>
                      <td style={{ padding: "0.5rem" }}>POST</td>
                      <td style={{ padding: "0.5rem" }}><code>/sub-accounts</code></td>
                      <td style={{ padding: "0.5rem" }}>X-Apollos-Secret</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "0.5rem" }}>Delete sub-account</td>
                      <td style={{ padding: "0.5rem" }}>DELETE</td>
                      <td style={{ padding: "0.5rem" }}><code>{"/sub-accounts/{id}"}</code></td>
                      <td style={{ padding: "0.5rem" }}>X-Apollos-Secret</td>
                    </tr>
                  </tbody>
                </table>

                <h3 style={{ marginTop: "1.5rem" }}>Copy-Paste Patterns</h3>
                <Accordion>
                  <AccordionTab header="Send SMS">
                    <CodeBlock>{`curl -X POST /api/v1/sms/send \\
  -H "Content-Type: application/json" \\
  -H "X-Apollos-Secret: your-api-key" \\
  -d '{"to":"256772123456","message":"Hello from Apollos!"}'`}</CodeBlock>
                  </AccordionTab>
                  <AccordionTab header="Bulk SMS">
                    <CodeBlock>{`curl -X POST /api/v1/sms/bulk \\
  -H "Content-Type: application/json" \\
  -H "X-Apollos-Secret: your-api-key" \\
  -d '{"to":["256772123456","254787375269"],"message":"Promo"}'`}</CodeBlock>
                  </AccordionTab>
                  <AccordionTab header="Send Email">
                    <CodeBlock>{`curl -X POST /api/v1/email/send \\
  -H "Content-Type: application/json" \\
  -H "X-Apollos-Secret: your-api-key" \\
  -d '{"to":"customer@example.com","subject":"Order confirmed","body_html":"<p>Thanks!</p>"}'`}</CodeBlock>
                  </AccordionTab>
                  <AccordionTab header="Initiate Payment">
                    <CodeBlock>{`curl -X POST /api/v1/payments/charge \\
  -H "Content-Type: application/json" \\
  -H "X-Apollos-Secret: your-api-key" \\
  -H "X-Apollos-Idempotency: unique-request-id" \\
  -d '{"amount":500000,"currency":"UGX","payment_method":"mobile_money","msisdn":"256772123456","reference":"INV-001"}'`}</CodeBlock>
                  </AccordionTab>
                  <AccordionTab header="Create Sub-Account">
                    <CodeBlock>{`curl -X POST /api/v1/sub-accounts \\
  -H "Content-Type: application/json" \\
  -H "X-Apollos-Secret: your-api-key" \\
  -d '{"username":"jane@company.com","firstName":"Jane","lastName":"Doe","emailAddress":"jane@company.com","phoneNumber":"256772123456","initialPassword":"temp-pass","roleIds":[]}'`}</CodeBlock>
                  </AccordionTab>
                </Accordion>

                <h3 style={{ marginTop: "1.5rem" }}>AI Pair-Programming Rules</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Always include <code>X-Apollos-Secret</code> in generated fetch / axios / curl examples.</li>
                  <li>Keep snippets minimal: one endpoint per example, no extra fields.</li>
                  <li>Use the exact field names from this tab (<code>to</code>, <code>message</code>, <code>amount</code>, <code>currency</code>, <code>payment_method</code>, <code>msisdn</code>, <code>reference</code>, <code>roleIds</code>).</li>
                  <li>If the user asks for authentication, distinguish <strong>public API</strong> (<code>X-Apollos-Secret</code>) from <strong>dashboard sessions</strong> (<code>Authorization: Bearer</code>).</li>
                  <li>Do not invent endpoints. If an endpoint is not listed here, tell the user to check the Admin Guide or API Reference tab.</li>
                </ul>

                <h3 style={{ marginTop: "1.5rem" }}>Sub-Account Billing Model</h3>
                <p>
                  Sub-accounts do <strong>not</strong> have their own wallets. Every charge (SMS,
                  payment fees, email quota) is billed to the <strong>parent</strong> account.
                  Resolve billing by walking up to the root account via the{" "}
                  <code>parent_user_id</code> chain. When you create a sub-account
                  programmatically, the response includes a one-time <code>apiKey</code>. Store
                  it securely; it is shown only once.
                </p>

                <h3 style={{ marginTop: "1.5rem" }}>Skills Reference</h3>
                <p>
                  A condensed <code>skills.md</code> is available for AI assistants to ingest
                  directly. Point your vibe-coding agent to <code>docs/skills.md</code> for the
                  machine-readable version of this guide, or download it here:
                </p>
                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
                  <button
                    onClick={() => downloadFile("/api/v1/developer-resources/skills.md", "skills.md")}
                    style={{
                      background: "#0f172a",
                      color: "#fff",
                      padding: "0.5rem 0.75rem",
                      borderRadius: "6px",
                      border: "none",
                      textDecoration: "none",
                      fontSize: "0.85rem",
                      cursor: "pointer",
                    }}
                  >
                    Download skills.md
                  </button>
                </div>
              </div>
            </TabPanel>

            {isSuperAdmin && (
              <TabPanel header="Admin Guide">
                <AdminDocumentation />
              </TabPanel>
            )}

            <TabPanel header="API Reference">
              <div style={{ padding: "0.5rem 0" }}>
                <h3>Authentication</h3>
                <p>Apollos supports two authentication methods depending on your use case.</p>

                <h4>1. API Key Authentication (Public API)</h4>
                <p>
                  Use this for server-to-server integrations. Include your API key in the{" "}
                  <code>X-Apollos-Secret</code> header. This authentication method is used for
                  public endpoints such as sending SMS, collecting payments, and managing
                  sub-accounts.
                </p>
                <p>
                  <strong>How to obtain:</strong> Dashboard &rarr; API Keys &rarr; New API Key.
                </p>

                <h4>2. JWT Token Authentication (Dashboard Sessions)</h4>
                <p>
                  Used for dashboard/portal access and internal API endpoints. Include the
                  token in the <code>Authorization</code> header as{" "}
                  <code>Bearer &lt;token&gt;</code>.
                </p>
                <CodeBlock>{`POST /api/v1/auth/login
Content-Type: application/json

{
  "username": "your-email@example.com",
  "password": "your-password"
}`}</CodeBlock>

                <p><strong>Response:</strong></p>
                <CodeBlock>{`{
  "user": {
    "id": 1,
    "firstName": "John",
    "lastName": "Doe",
    "emailAddress": "john@example.com",
    "superAdmin": false,
    "permissions": ["SEND_SMS", "VIEW_SMS_REPORTS"]
  },
  "accessToken": "eyJhbGciOiJIUzI1NiJ9...",
  "refreshToken": "abc123..."
}`}</CodeBlock>

                <p>Use the <code>accessToken</code> in subsequent requests:</p>
                <CodeBlock>{`Authorization: Bearer eyJhbGciOiJIUzI1NiJ9...`}</CodeBlock>

                <h3 style={{ marginTop: "1.5rem" }}>Developer Resources</h3>
                <p>
                  Download the official OpenAPI spec and Postman collection, or review the
                  integration guide to use Apollos in your own systems.
                </p>
                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
                  <button
                    onClick={() => downloadFile("/api/v1/developer-resources/openapi.json", "openapi.json")}
                    style={{
                      background: "#0f172a",
                      color: "#fff",
                      padding: "0.5rem 0.75rem",
                      borderRadius: "6px",
                      border: "none",
                      textDecoration: "none",
                      fontSize: "0.85rem",
                      cursor: "pointer",
                    }}
                  >
                    OpenAPI Spec
                  </button>
                  <button
                    onClick={() => downloadFile("/api/v1/developer-resources/apollos.postman_col.json", "apollos.postman_col.json")}
                    style={{
                      background: "#f97316",
                      color: "#fff",
                      padding: "0.5rem 0.75rem",
                      borderRadius: "6px",
                      border: "none",
                      textDecoration: "none",
                      fontSize: "0.85rem",
                      cursor: "pointer",
                    }}
                  >
                    Postman Collection
                  </button>
                </div>

                <h3 style={{ marginTop: "1.5rem" }}>Products & Gateways</h3>
                <p>
                  Apollos supports multiple product types, each with its own gateway configuration.
                  Gateways are managed in the dashboard under <strong>Payment Gateways</strong>.
                </p>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>
                      <th style={{ padding: "0.5rem", textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>Product Type</th>
                      <th style={{ padding: "0.5rem", textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>Description</th>
                      <th style={{ padding: "0.5rem", textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>Provider</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: "0.5rem", borderBottom: "1px solid #e2e8f0" }}><code>SMS_WALLET</code></td>
                      <td style={{ padding: "0.5rem", borderBottom: "1px solid #e2e8f0" }}>Wallet top-up and mobile money collections</td>
                      <td style={{ padding: "0.5rem", borderBottom: "1px solid #e2e8f0" }}>KaCyber Pay</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "0.5rem", borderBottom: "1px solid #e2e8f0" }}><code>DATA_BUNDLE</code></td>
                      <td style={{ padding: "0.5rem", borderBottom: "1px solid #e2e8f0" }}>Mobile data bundle purchases</td>
                      <td style={{ padding: "0.5rem", borderBottom: "1px solid #e2e8f0" }}>KaCyber Pay</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "0.5rem", borderBottom: "1px solid #e2e8f0" }}><code>BANK_TRANSFER</code></td>
                      <td style={{ padding: "0.5rem", borderBottom: "1px solid #e2e8f0" }}>Bank account payouts</td>
                      <td style={{ padding: "0.5rem", borderBottom: "1px solid #e2e8f0" }}>KaCyber Pay</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "0.5rem", borderBottom: "1px solid #e2e8f0" }}><code>MOBILE_MONEY_DISBURSEMENT</code></td>
                      <td style={{ padding: "0.5rem", borderBottom: "1px solid #e2e8f0" }}>Mobile money disbursements</td>
                      <td style={{ padding: "0.5rem", borderBottom: "1px solid #e2e8f0" }}>KaCyber Pay</td>
                    </tr>
                  </tbody>
                </table>

                <h3 style={{ marginTop: "1.5rem" }}>Money Transfers</h3>
                <p>
                  Send funds from your wallet to MTN/Airtel mobile money or bank accounts.
                  These endpoints require a dashboard JWT token.
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><code>GET /api/v1/transfers/banks</code> — Bank/telco directory</li>
                  <li><code>POST /api/v1/transfers/verification</code> — Request verification code</li>
                  <li><code>POST /api/v1/transfers</code> — Wire transfer</li>
                  <li><code>GET /api/v1/transfers/{'{id}'}</code> — Transfer status</li>
                  <li><code>GET /api/v1/transfers/stats</code> — Transfer statistics (optional ?method=mobile_money|bank)</li>
                </ul>

                <h3 style={{ marginTop: "1.5rem" }}>Error Codes</h3>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>Code</th>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>HTTP Status</th>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>Description</th>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>Resolution</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}><code>400</code></td>
                      <td style={{ padding: "0.5rem" }}>400</td>
                      <td style={{ padding: "0.5rem" }}>Invalid request payload</td>
                      <td style={{ padding: "0.5rem" }}>Check request body format</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}><code>401</code></td>
                      <td style={{ padding: "0.5rem" }}>401</td>
                      <td style={{ padding: "0.5rem" }}>Invalid or missing API credentials</td>
                      <td style={{ padding: "0.5rem" }}>Check your API key</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}><code>403</code></td>
                      <td style={{ padding: "0.5rem" }}>403</td>
                      <td style={{ padding: "0.5rem" }}>Access denied</td>
                      <td style={{ padding: "0.5rem" }}>Verify your account permissions</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}><code>404</code></td>
                      <td style={{ padding: "0.5rem" }}>404</td>
                      <td style={{ padding: "0.5rem" }}>Resource not found</td>
                      <td style={{ padding: "0.5rem" }}>Verify the reference ID</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}><code>402</code></td>
                      <td style={{ padding: "0.5rem" }}>402</td>
                      <td style={{ padding: "0.5rem" }}>Insufficient credits or quota</td>
                      <td style={{ padding: "0.5rem" }}>Top up your wallet or upgrade your email subscription</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}><code>409</code></td>
                      <td style={{ padding: "0.5rem" }}>409</td>
                      <td style={{ padding: "0.5rem" }}>Duplicate charge / idempotency key already used</td>
                      <td style={{ padding: "0.5rem" }}>Use a new idempotency key for each charge attempt</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}><code>422</code></td>
                      <td style={{ padding: "0.5rem" }}>422</td>
                      <td style={{ padding: "0.5rem" }}>Unprocessable entity / validation failure</td>
                      <td style={{ padding: "0.5rem" }}>Check request payload and field values</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}><code>429</code></td>
                      <td style={{ padding: "0.5rem" }}>429</td>
                      <td style={{ padding: "0.5rem" }}>Rate limit exceeded</td>
                      <td style={{ padding: "0.5rem" }}>Wait before retrying; check the Retry-After header</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "0.5rem" }}><code>500</code></td>
                      <td style={{ padding: "0.5rem" }}>500</td>
                      <td style={{ padding: "0.5rem" }}>Internal error</td>
                      <td style={{ padding: "0.5rem" }}>Contact support</td>
                    </tr>
                  </tbody>
                </table>

                <h3 style={{ marginTop: "1.5rem" }}>Rate Limits</h3>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>Endpoint</th>
                      <th style={{ textAlign: "left", padding: "0.5rem" }}>Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}>SMS Sending</td>
                      <td style={{ padding: "0.5rem" }}>100 messages per minute per API key</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "0.5rem" }}>Status Checks</td>
                      <td style={{ padding: "0.5rem" }}>60 requests per minute</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "0.5rem" }}>Account Lookup</td>
                      <td style={{ padding: "0.5rem" }}>30 requests per minute</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </TabPanel>
          </TabView>
        </Card>
      </div>
    </div>
  );
};

export default Documentation;
